/**
 * Pasos de la guía de la lista de ventas (VentasQr). Se usan en "Ventas QR" y dentro de
 * "Mis clientes"; los filtros que el usuario no ve por su rol se saltan solos.
 *
 * @param {(es: string, en: string) => string} t
 * @param {{ propio?: boolean }} opciones - propio: la lista son las ventas del QR del usuario
 */
export const pasosVentas = (t, { propio = false } = {}) => [
  {
    el: "v-filtros",
    titulo: t("Filtros", "Filters"),
    texto: propio
      ? t("Elige la campaña, el estado y las fechas. Por defecto ves lo del mes actual.", "Pick the campaign, status and dates. By default you see the current month.")
      : t(
          "Filtra por promotor, equipo de un supervisor, campaña, estado y fechas. Por defecto ves lo del mes actual.",
          "Filter by promoter, a supervisor's team, campaign, status and dates. By default you see the current month."
        ),
  },
  {
    el: "v-estado",
    titulo: t("Estado de la venta", "Sale status"),
    texto: t(
      "«Pagadas y registros» es lo que ya cuenta: ventas cobradas y registros gratis verificados.\n«Pendientes»: el cliente abrió el pago y no lo terminó.\n«Sin verificar»: se registró gratis pero no puso el PIN del SMS.",
      "«Paid and registrations» is what already counts: charged sales and verified free registrations.\n«Pending»: the customer opened the payment and didn't finish.\n«Unverified»: registered for free but didn't enter the SMS PIN."
    ),
  },
  {
    el: "v-buscar",
    titulo: t("Buscar", "Search"),
    texto: t("Después de cambiar los filtros presiona Buscar para actualizar la lista.", "After changing the filters press Search to refresh the list."),
  },
  {
    el: "v-excel",
    titulo: t("Exportar a Excel", "Export to Excel"),
    texto: propio
      ? t("Descarga en Excel lo que tienes filtrado.", "Download what you have filtered as Excel.")
      : t(
          "Descarga en Excel lo que tienes filtrado. Cada venta lleva al promotor y al supervisor que tenía al venderse: es la base para las comisiones.",
          "Download what you have filtered as Excel. Each sale has the promoter and the supervisor they had at the time: it's the basis for commissions."
        ),
  },
  {
    el: "v-resumen",
    titulo: t("Resumen", "Summary"),
    texto: t("Cuántas ventas hay con estos filtros, el total cobrado y los registros gratis.", "How many sales match these filters, the total charged and the free registrations."),
  },
  {
    el: "v-tabla",
    titulo: propio ? t("Tus clientes", "Your customers") : t("Las ventas", "The sales"),
    texto: t(
      "Cada fila es un cliente con su campaña, monto y estado. En «Entrega» ves el certificado que se le envió por correo. Ordena con los encabezados.",
      "Each row is a customer with their campaign, amount and status. «Delivery» shows the certificate emailed to them. Sort with the headers."
    ),
  },
];

/**
 * Guía de "Campañas QR" (solo admin). Abre un formulario vacío para recorrerlo.
 *
 * @param {(es: string, en: string) => string} t
 * @param {{ formulario: Function, lista: Function }} pantalla - abren o cierran el formulario
 */
export const pasosCampanas = (t, { formulario, lista }) => [
  {
    el: null,
    antes: lista,
    titulo: t("Campañas QR", "QR Campaigns"),
    texto: t(
      "Una campaña es lo que el cliente ve y compra (o recibe gratis) al escanear el QR de un promotor. Te mostramos cómo crear una.",
      "A campaign is what the customer sees and buys (or gets for free) when scanning a promoter's QR. Let's see how to create one."
    ),
  },
  {
    el: "nueva",
    antes: lista,
    titulo: t("Nueva campaña", "New campaign"),
    texto: t("Todo empieza aquí. Te abrimos el formulario para ver cada parte; no se guarda nada.", "It all starts here. We'll open the form to go through each part; nothing is saved."),
  },
  {
    el: "c-idiomas",
    antes: formulario,
    titulo: t("En los dos idiomas", "In both languages"),
    texto: t(
      "Nombre, descripción y términos van en inglés y en español. El checkout abre en inglés y el cliente puede cambiar a español.",
      "Name, description and terms go in English and Spanish. The checkout opens in English and the customer can switch to Spanish."
    ),
  },
  {
    el: "c-fotos",
    antes: formulario,
    titulo: t("Fotos", "Photos"),
    texto: t(
      "La primera es la portada: se ve primero en el checkout y la muestra Stripe al pagar. Puedes agregar 4 más y cambiar la portada con la estrella.",
      "The first one is the cover: it's shown first on the checkout and by Stripe at payment. You can add 4 more and change the cover with the star."
    ),
  },
  {
    el: "c-pdf",
    antes: formulario,
    titulo: t("PDF (opcional)", "PDF (optional)"),
    texto: t("Un PDF con la información completa, uno por idioma. Si subes solo uno, se muestra en ambos.", "A PDF with the full details, one per language. If you upload only one, it's shown in both."),
  },
  {
    el: "c-gratis",
    antes: formulario,
    titulo: t("¿Se cobra o es gratis?", "Paid or free?"),
    texto: t(
      "Apagado: el cliente paga con tarjeta.\nEncendido: no hay cobro; el cliente se registra, verifica su celular con un PIN por SMS y recibe su regalo.",
      "Off: the customer pays by card.\nOn: no charge; the customer registers, verifies their phone with an SMS PIN and gets their gift."
    ),
  },
  {
    el: "c-precio",
    antes: formulario,
    titulo: t("Precio", "Price"),
    texto: t("Lo que se cobra, en dólares. Si la campaña es gratis, es el valor del regalo, que se muestra tachado.", "What is charged, in dollars. If the campaign is free, it's the gift value, shown crossed out."),
  },
  {
    el: "c-fuente",
    antes: formulario,
    titulo: t("¿Cómo nos conoció?", "How did they hear about us?"),
    texto: t(
      "Se asigna sola a cada venta o registro de esta campaña y viaja a HubSpot. Sirve para saber de dónde vienen los clientes (ej.: Expo Cancún 2026).",
      "It's assigned to every sale or registration of this campaign and sent to HubSpot. It tells you where customers come from (e.g.: Expo Cancun 2026)."
    ),
  },
  {
    el: "c-entrega",
    antes: formulario,
    titulo: t("Qué recibe el cliente", "What the customer gets"),
    texto: t(
      "«Regalo por correo» le envía un certificado al pagar o registrarse. Las plantillas se crean en Cupones y regalos → Plantillas de regalo, con «Disponible para: Campañas QR».",
      "«Gift by email» sends a certificate when they pay or register. Templates are created in Coupons & gifts → Gift templates, with «Available for: QR campaigns»."
    ),
  },
  {
    el: "c-vigencia",
    antes: formulario,
    titulo: t("Vigencia", "Validity"),
    texto: t("Fechas opcionales: fuera de ellas la campaña no aparece en el checkout. Sin fechas, no vence.", "Optional dates: outside them the campaign doesn't show on the checkout. With no dates, it doesn't expire."),
  },
  {
    el: "c-activa",
    antes: formulario,
    titulo: t("Activa o inactiva", "Active or inactive"),
    texto: t("Desactivarla la quita del checkout de inmediato, sin borrar sus ventas.", "Disabling it removes it from the checkout right away, without deleting its sales."),
  },
  {
    el: "c-asignacion",
    antes: formulario,
    titulo: t("¿Quién la vende?", "Who sells it?"),
    texto: t(
      "«Todos los promotores» o solo los que elijas. Si eliges a un supervisor, le llega a todo su equipo y él decide qué vende cada agente. Si un QR tiene varias campañas, el cliente elige.",
      "«All promoters» or only the ones you choose. If you choose a supervisor, it reaches their whole team and they decide what each agent sells. If a QR has several campaigns, the customer chooses."
    ),
  },
  {
    el: "c-guardar",
    antes: formulario,
    titulo: t("Guardar", "Save"),
    texto: t("Al guardar, la campaña aparece en el checkout de los QR asignados.", "Once saved, the campaign shows on the checkout of the assigned QRs."),
  },
  {
    el: "editar",
    antes: lista,
    titulo: t("Editar", "Edit"),
    texto: t("Con el lápiz cambias cualquier dato de una campaña ya creada.", "Use the pencil to change any detail of an existing campaign."),
  },
  {
    el: "inactivas",
    antes: lista,
    titulo: t("Campañas inactivas", "Inactive campaigns"),
    texto: t("Por defecto solo ves las activas; enciende esto para ver también las inactivas.", "By default you only see active ones; turn this on to also see inactive ones."),
  },
];

/**
 * Guía de "Promotores QR" (admin) o "Mi equipo" (supervisor). Los botones de fila se
 * señalan en la primera fila que los tiene.
 *
 * @param {(es: string, en: string) => string} t
 * @param {{ esAdmin: boolean }} opciones
 */
export const pasosPromotores = (t, { esAdmin }) => [
  {
    el: null,
    titulo: esAdmin ? t("Promotores QR", "QR Promoters") : t("Mi equipo", "My team"),
    texto: esAdmin
      ? t(
          "Aquí das de alta a quienes venden con QR: supervisores, agentes y establecimientos. Cada uno tiene su propio QR y las ventas que entran por él se le atribuyen.",
          "Here you add the people who sell with a QR: supervisors, agents and businesses. Each one has their own QR and the sales through it are attributed to them."
        )
      : t(
          "Aquí están tú, tus agentes y tus establecimientos. Cada uno vende con su propio QR y tú ves las ventas de todos.",
          "Here are you, your agents and your businesses. Each one sells with their own QR and you see everyone's sales."
        ),
  },
  {
    el: "p-filtro",
    titulo: t("Ver por equipo", "View by team"),
    texto: t(
      "Filtra la lista: solo supervisores, agentes sin supervisor, establecimientos o el equipo de un supervisor.",
      "Filter the list: supervisors only, agents without a supervisor, businesses or one supervisor's team."
    ),
  },
  {
    el: "p-agregar",
    titulo: esAdmin ? t("Agregar promotor", "Add promoter") : t("Agregar agente", "Add agent"),
    texto: esAdmin
      ? t(
          "Eliges si es agente o supervisor y, si es agente, quién es su supervisor. Se crea su usuario y su QR. La contraseña se muestra una sola vez: cópiala y compártesela.",
          "Choose whether they're an agent or a supervisor and, for an agent, who their supervisor is. Their user and QR are created. The password is shown only once: copy it and share it."
        )
      : t(
          "El agente queda en tu equipo con su usuario y su QR. La contraseña se muestra una sola vez: cópiala y compártesela.",
          "The agent joins your team with their user and QR. The password is shown only once: copy it and share it."
        ),
  },
  {
    el: "p-establecimiento",
    titulo: t("Agregar establecimiento", "Add business"),
    texto: t(
      "Un negocio (hotel, tienda, restaurante) con su QR en el mostrador. No entra al backoffice: sus registros y ventas los ve su supervisor.",
      "A business (hotel, store, restaurant) with its QR at the counter. It doesn't log in: its supervisor sees its registrations and sales."
    ),
  },
  {
    el: "p-link",
    titulo: t("Código y link del QR", "QR code and link"),
    texto: t("Es lo que abre el cliente al escanear. El código no cambia aunque se desactive el promotor.", "It's what the customer opens when scanning. The code doesn't change even if the promoter is disabled."),
  },
  {
    el: "p-activo",
    titulo: t("Activo", "Active"),
    texto: esAdmin
      ? t("Al desactivar a alguien su QR deja de recibir pagos de inmediato. Sus ventas pasadas se conservan.", "Disabling someone stops their QR from taking payments right away. Their past sales are kept.")
      : t(
          "Puedes desactivar el QR de tus agentes; deja de recibir pagos de inmediato. Tu propio QR solo lo desactiva un administrador.",
          "You can disable your agents' QR; it stops taking payments right away. Only an administrator can disable your own QR."
        ),
  },
  {
    el: "p-ver-qr",
    titulo: t("Ver QR", "View QR"),
    texto: t(
      "Descarga el QR en PNG (para mandarlo por WhatsApp o redes), en SVG (para imprimir en grande) o copia el link.",
      "Download the QR as PNG (to send by WhatsApp or social media), as SVG (to print large) or copy the link."
    ),
  },
  {
    el: "p-cartel",
    titulo: t("Cartel para imprimir", "Printable sign"),
    texto: t("Un cartel listo con el QR, ideal para el mostrador de un establecimiento.", "A ready-made sign with the QR, ideal for a business's counter."),
  },
  {
    el: "p-campanas",
    titulo: t("Campañas que vende", "Campaigns they sell"),
    texto: t(
      "Para un agente con supervisor: eliges cuáles de las campañas del equipo muestra su QR. «Todas» incluye también las que el equipo reciba después.",
      "For an agent with a supervisor: choose which of the team's campaigns their QR shows. «All» also includes the ones the team gets later."
    ),
  },
  {
    el: "p-equipo",
    titulo: t("Tipo y supervisor", "Type and supervisor"),
    texto: t(
      "Convierte a un agente en supervisor o cámbialo de equipo. Las ventas ya hechas siguen contando para el supervisor que tenía; si cambia su rol, tiene que volver a iniciar sesión.",
      "Turn an agent into a supervisor or move them to another team. Past sales still count for the supervisor they had; if their role changes, they must log in again."
    ),
  },
];

/**
 * Guía de "Mis clientes". El admin ve ahí la lista de todos; el promotor, su QR y sus clientes.
 *
 * @param {(es: string, en: string) => string} t
 * @param {{ esAdmin: boolean }} opciones
 */
export const pasosMisClientes = (t, { esAdmin }) =>
  esAdmin
    ? [
        {
          el: null,
          titulo: t("Clientes", "Customers"),
          texto: t("Las compras y registros de todos los promotores. Te mostramos cómo filtrar y exportar.", "Purchases and registrations from all promoters. Let's see how to filter and export."),
        },
        ...pasosVentas(t),
      ]
    : [
        {
          el: null,
          titulo: t("Mis clientes", "My customers"),
          texto: t("Aquí tienes tu QR de cobro y los clientes que compraron o se registraron con él.", "Here you have your payment QR and the customers who bought or registered with it."),
        },
        {
          el: "m-qr",
          titulo: t("Tu QR de cobro", "Your payment QR"),
          texto: t(
            "Es único: todo lo que entra por él se te atribuye. Si sale «Desactivado», habla con tu supervisor.",
            "It's unique: everything that comes through it is attributed to you. If it says «Disabled», talk to your supervisor."
          ),
        },
        {
          el: "m-botones",
          titulo: t("Compártelo", "Share it"),
          texto: t("PNG para mandarlo por WhatsApp o redes, SVG para imprimirlo en grande, o copia el link.", "PNG to send by WhatsApp or social media, SVG to print it large, or copy the link."),
        },
        ...pasosVentas(t, { propio: true }),
      ];
